/**
 * The same tokens as tokens.css, for code that needs them as values: canvas
 * rendering, the Tauri launcher's Rust side, chart colours, OG image
 * generation. tokens.css remains the source consumed by the DOM; these must be
 * kept in step with it.
 */

import type { SiteId } from './sites';

export const BRAND = {
  /** The store: games, purchase, verified. Primary. */
  build: '#2B3DFF',
  /** Creators: pages, board, commission, attribution. */
  hot: '#FF2D6F',
  /** First Hundred, playtests, anything counting down. */
  solar: '#FF7A00',
  /** Live only: streaming now, slots left, current version. Under 5% of a layout. */
  volt: '#C6FF00',
  ink: '#0B0B12',
  paper: '#FBFAF6',
} as const;

export const INK_RAMP = {
  70: '#4A4A57',
  40: '#8C8C99',
  15: '#D9D9E0',
  7: '#EDEDF2',
} as const;

/**
 * Which text colour each signal colour takes. Volt and Solar take Ink type,
 * never white; both fail contrast against Paper at text sizes.
 */
export const ON_BRAND = {
  build: BRAND.paper,
  hot: BRAND.paper,
  solar: BRAND.ink,
  volt: BRAND.ink,
} as const;

/**
 * One channel colour per surface. A creator page is Hot throughout, a store
 * page is Build throughout. Mixing channels on one surface is the mistake this
 * mapping exists to prevent.
 */
export const CHANNEL = {
  store: BRAND.build,
  creator: BRAND.hot,
  countdown: BRAND.solar,
  live: BRAND.volt,
} as const;

export type Channel = keyof typeof CHANNEL;

/**
 * The accent each audience space carries, as `tokens.css` resolves it.
 *
 * The same three facts as `[data-gb-space='...']`, in a form something outside
 * a stylesheet can read. Two things need them and neither is CSS: a
 * `<meta name="theme-color">`, which is what paints a mobile address bar and
 * the Android task switcher, and the favicon generator in
 * `tools/brand-mark.mjs`, which draws the top bar of each side's mark in it.
 *
 * **Not `CHANNEL` above**, which reads as though it answers this and does not.
 * That mapping is older than the split into three spaces: it hands the store
 * Build, and Build has belonged to developers alone since — a panel's colour is
 * only wayfinding if no two panels share one. `CHANNEL` is about what a
 * *thing* means (a countdown is Solar, a live badge is Volt); this is about
 * where you are standing.
 *
 * Keyed by `SiteId` rather than by a name of its own, because these are the
 * three sites and a fourth key here would be a space with no ground.
 */
export const SPACE_ACCENT: Readonly<Record<SiteId, string>> = {
  store: BRAND.solar,
  creators: BRAND.hot,
  developers: BRAND.build,
} as const;

export const FONTS = {
  display: 'Archivo',
  text: 'Archivo',
  /** Every number in the product. */
  mono: 'Martian Mono',
} as const;

/** Section 7.4. Ground colour of a mission patch carries meaning. */
export const PATCH_GROUND = {
  /** An original launch drop. */
  original: BRAND.solar,
  /** Slots 1 to 10. */
  founding: BRAND.build,
  /** A relaunch drop. */
  relaunch: BRAND.ink,
  /** An expired claim. */
  expired: INK_RAMP[40],
} as const;

export type PatchGround = keyof typeof PATCH_GROUND;
